/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Overlay, OVERLAY_CONTENT_MOD } from '@veeam/components';

import { getPortalContainer } from '../../helpers/react-helpers';

import type { UseFullPageActions, UseFullPageProps } from './interfaces';

const StyledOverlay = styled(Overlay)`
    background-color: ${({ theme }) => theme.colors.backgrounds.normal};
`;

// Return "T extends unknown" when ESLint fix this rule https://github.com/typescript-eslint/typescript-eslint/issues/4062
export const useFullPage = <T,>(props: UseFullPageProps<T>): [React.ReactElement, UseFullPageActions<T>] => {
    const {
        initialActiveState = false,
        render,
        onClose,
        headerHeight = 48,
        isExtMode = false,
        zIndexForFullPageWindow = 99,
        overlayProps,
    } = props;
    const dataRef = useRef<T>(null);
    const [isFullPageActive, setIsFullPageActive] = useState(initialActiveState);

    const activate = (userData) => {
        dataRef.current = userData;
        setIsFullPageActive(true);
    };

    const deactivate = () => {
        dataRef.current = null;
        setIsFullPageActive(false);
    };

    const toggle = userData => isFullPageActive ? deactivate() : activate(userData);

    const fullPageWrapper = (
        isFullPageActive && (
            createPortal(
                <StyledOverlay
                    fixed
                    style={{
                        top: headerHeight,
                        height: `calc(100% - ${headerHeight}px)`,
                    }}
                    {...(isExtMode ? { zIndex: zIndexForFullPageWindow } : {})}
                    contentMod={OVERLAY_CONTENT_MOD.fill}
                    {...overlayProps}
                >
                    {render?.({
                        onClose: () => {
                            deactivate();
                            onClose?.();
                        },
                        data: dataRef.current,
                    })}
                </StyledOverlay>,
                getPortalContainer(),
            )
        )
    );

    return [
        fullPageWrapper,
        {
            activate,
            deactivate,
            toggle,
            isActive: isFullPageActive,
        },
    ];
};
